import React from 'react';
import { clsx } from 'clsx';
import type { CardProps } from '@/types/ui';

/**
 * Webcoda-styled Card component
 * Flexible container with header, body, and footer sections
 */
export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  header,
  footer,
  padding = 'md',
  shadow = 'md',
  border = true,
  hoverable = false,
  className,
  children,
  'data-testid': testId,
  ...props
}) => {
  // Base card classes
  const baseClasses = [
    'bg-white',
    'dark:bg-gray-800',
    'rounded-xl',
    'overflow-hidden',
    'transition-all',
    'duration-200',
  ];

  // Shadow classes
  const shadowClasses = {
    none: [],
    sm: ['shadow-soft'],
    md: ['shadow-medium'],
    lg: ['shadow-large'],
  };

  // Border classes
  const borderClasses = border ? ['border', 'border-gray-200', 'dark:border-gray-700'] : [];

  // Hover classes
  const hoverClasses = hoverable ? ['hover:shadow-large', 'hover:-translate-y-0.5', 'cursor-pointer'] : [];

  const cardClasses = clsx(
    baseClasses,
    shadowClasses[shadow],
    borderClasses,
    hoverClasses,
    className
  );

  // Padding classes for different sections
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const headerPaddingClasses = {
    none: '',
    sm: 'px-4 py-3',
    md: 'px-6 py-4',
    lg: 'px-8 py-6',
  };

  const bodyPaddingClasses = paddingClasses[padding];
  const headerPaddingClass = headerPaddingClasses[padding];

  return (
    <div 
      className={cardClasses} 
      data-testid={testId}
      {...props}
    >
      {/* Custom Header or Title/Subtitle Header */}
      {(header || title || subtitle) && (
        <div className={clsx(headerPaddingClass, 'border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50')}>
          {header || (
            <div className="space-y-1">
              {title && (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-6">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Body */}
      {children && (
        <div className={bodyPaddingClasses}>
          {children}
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div className={clsx(headerPaddingClass, 'border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50')}>
          {footer}
        </div>
      )}
    </div>
  );
};

/**
 * Card.Header - Standalone header component
 */
export const CardHeader: React.FC<{
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}> = ({ title, subtitle, actions, className, children }) => (
  <div className={clsx('flex items-center justify-between', className)}>
    <div className="min-w-0 flex-1">
      {children || (
        <div className="space-y-1">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-6 truncate">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
    {actions && (
      <div className="flex-shrink-0 ml-4">
        {actions}
      </div>
    )}
  </div>
);

/**
 * Card.Body - Standalone body component
 */
export const CardBody: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <div className={clsx('flex-1', className)}>
    {children}
  </div>
);

/**
 * Card.Footer - Standalone footer component
 */
export const CardFooter: React.FC<{
  className?: string;
  children: React.ReactNode;
  justify?: 'start' | 'center' | 'end' | 'between';
}> = ({ className, children, justify = 'end' }) => {
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div className={clsx('flex items-center', justifyClasses[justify], className)}>
      {children}
    </div>
  );
};

// Attach sub-components to main Card component
Object.assign(Card, { Header: CardHeader });
Object.assign(Card, { Body: CardBody });
Object.assign(Card, { Footer: CardFooter });